import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import CommissionModal from './CommissionModal';

const { FiEdit, FiTrash2, FiDollarSign, FiMoreVertical, FiUsers } = FiIcons;

const ClientManagementTable = ({
  clients, 
  onStatusUpdate, 
  onCommissionUpdate, 
  onDeleteClient 
}) => {
  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const statusConfig = {
    nuovo_lead: { label: 'Nuovo Lead', color: 'blue' },
    in_trattativa: { label: 'In Trattativa', color: 'orange' },
    cliente_acquisito: { label: 'Cliente Acquisito', color: 'green' },
    pagato: { label: 'Pagato', color: 'emerald' },
    commissionato: { label: 'Commissionato', color: 'purple' }
  };

  const statusOptions = [
    { value: 'nuovo_lead', label: 'Nuovo Lead' },
    { value: 'in_trattativa', label: 'In Trattativa' },
    { value: 'cliente_acquisito', label: 'Cliente Acquisito' },
    { value: 'pagato', label: 'Pagato' }
  ];

  const handleCommissionClick = (client) => {
    setSelectedClient(client);
    setShowCommissionModal(true);
  };

  const handleCommissionSubmit = (commission) => {
    onCommissionUpdate(selectedClient.id, commission);
    setShowCommissionModal(false);
    setSelectedClient(null);
  };

  const toggleRowExpansion = (clientId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(clientId)) {
      newExpanded.delete(clientId);
    } else {
      newExpanded.add(clientId);
    }
    setExpandedRows(newExpanded);
  };

  const getStatusBadge = (status) => {
    const config = statusConfig[status];
    const colorClasses = {
      blue: 'bg-blue-50 text-blue-700 border-blue-200',
      orange: 'bg-orange-50 text-orange-700 border-orange-200',
      green: 'bg-green-50 text-green-700 border-green-200',
      emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      purple: 'bg-purple-50 text-purple-700 border-purple-200'
    };

    return (
      <span className={`px-2 py-1 rounded-lg text-xs font-medium border ${colorClasses[config.color]}`}>
        {config.label}
      </span>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4 }}
      className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
    >
      <div className="p-6 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Gestione Clienti</h2>
        <p className="text-sm text-gray-600 mt-1">
          {clients.length} clienti totali
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Cliente
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Venditore
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Valore
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Stato
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Data
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Commissione
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Azioni
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {clients.map((client, index) => (
              <React.Fragment key={client.id}>
                <motion.tr
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="hover:bg-gray-50"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {client.contactName}
                      </div>
                      <div className="text-sm text-gray-500">
                        {client.clientName}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {client.userName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    €{client.contractValue.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <select
                      value={client.status}
                      onChange={(e) => onStatusUpdate(client.id, e.target.value)}
                      className="text-xs border-0 bg-transparent focus:ring-0 font-medium"
                    >
                      {statusOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {format(client.createdAt, 'dd/MM/yyyy', { locale: it })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {client.commission ? (
                      <span className="text-sm font-medium text-emerald-600">
                        €{client.commission.toLocaleString()}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center space-x-2">
                      {(client.status === 'pagato' && !client.commission) && (
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleCommissionClick(client)}
                          className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"
                          title="Aggiungi Commissione"
                        >
                          <SafeIcon icon={FiDollarSign} />
                        </motion.button>
                      )}
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => toggleRowExpansion(client.id)}
                        className="p-1 text-gray-400 hover:bg-gray-50 rounded"
                        title="Dettagli"
                      >
                        <SafeIcon icon={FiMoreVertical} />
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => onDeleteClient(client.id)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                        title="Elimina"
                      >
                        <SafeIcon icon={FiTrash2} />
                      </motion.button>
                    </div>
                  </td>
                </motion.tr>
                
                <AnimatePresence>
                  {expandedRows.has(client.id) && (
                    <motion.tr
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                    >
                      <td colSpan="7" className="px-6 py-4 bg-gray-50">
                        <div className="text-sm text-gray-600">
                          <strong>Note:</strong> {client.notes || 'Nessuna nota disponibile'}
                        </div>
                      </td>
                    </motion.tr>
                  )}
                </AnimatePresence>
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {clients.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <SafeIcon icon={FiUsers} className="text-4xl mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Nessun cliente trovato</h3>
          <p className="text-gray-500">I clienti aggiunti dai venditori appariranno qui</p>
        </div>
      )}

      <CommissionModal
        isOpen={showCommissionModal}
        client={selectedClient}
        onClose={() => {
          setShowCommissionModal(false);
          setSelectedClient(null);
        }}
        onSubmit={handleCommissionSubmit}
      />
    </motion.div>
  );
};

export default ClientManagementTable;